import { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import toast from 'react-hot-toast';
import {
  BanknotesIcon, PlusIcon, PencilSquareIcon, ArchiveBoxIcon, XMarkIcon,
} from '@heroicons/react/24/outline';

const ROLE_OPTS = [
  { value: '', label: 'All roles' },
  { value: 'director', label: 'Director' },
  { value: 'admin', label: 'Office Admin' },
  { value: 'teacher', label: 'Full-Time Teacher' },
  { value: 'float', label: 'Part-Time Float' },
  { value: 'cook', label: 'Cook' },
];

export default function PayRulesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/pay-rules');
      setRows(data);
    } catch { toast.error('Failed to load pay rules'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const archive = async (id) => {
    if (!confirm('Archive this rule? Premium hours will stop counting under it from now on.')) return;
    try {
      await api.delete(`/pay-rules/${id}`);
      toast.success('Archived');
      load();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="animate-slide-up">
      <header className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BanknotesIcon className="w-6 h-6" /> Pay Rules
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Premium-hour multipliers. Default rule: anything scheduled at or after 6pm pays 1.25x.
            Editable per role. Premium minutes show on each shift cell + roll up in payroll.
          </p>
        </div>
        <button onClick={() => setEditing('new')}
          className="bg-orange-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-orange-600 inline-flex items-center gap-1">
          <PlusIcon className="w-4 h-4" /> Add rule
        </button>
      </header>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center text-sm text-gray-500">
          No active pay rules. Click "Add rule" to set up your first premium window.
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-xs text-gray-500 uppercase bg-gray-50">
                <th className="text-left px-5 py-3">Label</th>
                <th className="text-left px-5 py-3">Role</th>
                <th className="text-left px-5 py-3">Window</th>
                <th className="text-left px-5 py-3">Days</th>
                <th className="text-left px-5 py-3">Multiplier</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map(r => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-5 py-3 font-medium">{r.label}</td>
                  <td className="px-5 py-3 text-gray-600 capitalize">{r.role?.replace('_', ' ') || 'All'}</td>
                  <td className="px-5 py-3 font-mono text-xs">
                    {r.starts_at?.slice(0, 5)} – {r.ends_at?.slice(0, 5) || 'midnight'}
                  </td>
                  <td className="px-5 py-3 text-xs text-gray-600">
                    {r.applies_weekdays && r.applies_weekends ? 'Every day'
                      : r.applies_weekdays ? 'Weekdays only'
                      : r.applies_weekends ? 'Weekends only'
                      : 'Disabled'}
                  </td>
                  <td className="px-5 py-3">
                    <span className="text-purple-700 font-semibold">×{Number(r.multiplier).toFixed(2)}</span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => setEditing(r)}
                      className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 mr-1">
                      <PencilSquareIcon className="w-4 h-4" />
                    </button>
                    <button onClick={() => archive(r.id)}
                      className="p-1.5 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600">
                      <ArchiveBoxIcon className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <RuleModal rule={editing === 'new' ? {} : editing}
          isNew={editing === 'new'}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function RuleModal({ rule, isNew, onClose, onSaved }) {
  const [form, setForm] = useState({
    label: rule.label || '',
    role: rule.role || '',
    starts_at: rule.starts_at?.slice(0, 5) || '18:00',
    ends_at: rule.ends_at?.slice(0, 5) || '',
    multiplier: rule.multiplier != null ? String(rule.multiplier) : '1.25',
    applies_weekdays: rule.applies_weekdays !== false,
    applies_weekends: rule.applies_weekends !== false,
    notes: rule.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.label || !form.starts_at || !form.multiplier) {
      toast.error('Label, start time, and multiplier are required');
      return;
    }
    const m = Number(form.multiplier);
    if (Number.isNaN(m) || m <= 0) { toast.error('Multiplier must be > 0'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        role: form.role || null,
        ends_at: form.ends_at || null,
        multiplier: m,
      };
      if (isNew) {
        await api.post('/pay-rules', payload);
        toast.success('Rule added');
      } else {
        await api.patch(`/pay-rules/${rule.id}`, payload);
        toast.success('Updated');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-lg font-semibold">{isNew ? 'Add pay rule' : 'Edit pay rule'}</h2>
          <button onClick={onClose}><XMarkIcon className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Label">
            <input value={form.label} onChange={(e) => setForm({...form, label: e.target.value})}
              placeholder='e.g. "Evening premium" or "Weekend"'
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
          </Field>
          <Field label="Applies to role">
            <select value={form.role} onChange={(e) => setForm({...form, role: e.target.value})}
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm bg-white">
              {ROLE_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Window starts at">
              <input type="time" value={form.starts_at} onChange={(e) => setForm({...form, starts_at: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
            </Field>
            <Field label="Ends at (blank = midnight)">
              <input type="time" value={form.ends_at} onChange={(e) => setForm({...form, ends_at: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
            </Field>
          </div>
          <Field label="Multiplier (1.25 = 25% premium)">
            <input type="number" step="0.05" value={form.multiplier}
              onChange={(e) => setForm({...form, multiplier: e.target.value})}
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
          </Field>
          <div className="flex gap-4">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={form.applies_weekdays}
                onChange={(e) => setForm({...form, applies_weekdays: e.target.checked})}
                className="accent-orange-500" />
              Weekdays
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={form.applies_weekends}
                onChange={(e) => setForm({...form, applies_weekends: e.target.checked})}
                className="accent-orange-500" />
              Weekends
            </label>
          </div>
          <Field label="Notes (optional)">
            <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}
              rows={2}
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm resize-y" />
          </Field>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="px-4 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-xl">Cancel</button>
            <button onClick={save} disabled={saving}
              className="bg-orange-500 text-white px-4 py-1.5 rounded-xl text-sm font-medium hover:bg-orange-600 disabled:opacity-50">
              {saving ? 'Saving…' : (isNew ? 'Add' : 'Save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {children}
    </div>
  );
}
